import {useQuery} from "@tanstack/react-query";
import {useClientV2} from "../../utils/hooks";

export type OfferingCategory =
  | "services"
  | "liveEvents"
  | "digitalProducts"
  | "productServices"
  | "subscriptions";

export interface OfferingItem {
  id: number;
  name: string;
  description?: string | null;
  about?: string | null;
  imageUrl?: string | null;
  coverImage?: string | null;
  coverPicture?: string | null;
  currency?: string | null;
  priceCurrency?: string | null;
  callPriceCurrency?: string | null;
  amount?: number | null;
  price?: number | null;
  callPrice?: number | null;
  currencies?: Array<{
    currency?: string | null;
    callPriceCurrency?: string | null;
    amount?: number | null;
    callPrice?: number | null;
    isAutoConverted?: boolean;
    isBase?: boolean;
  }>;
  date?: string | null;
  eventDate?: string | null;
  startDate?: string | null;
  activeSession?: {startDate?: string | null} | null;
  sessionInfo?: Array<{startDate?: string | null}> | null;
  session_info?: Array<{startDate?: string | null}> | null;
  coverImages?: Array<{mediaUrl?: string | null}> | null;
}

export interface GetOfferingsResponse {
  status: boolean;
  message: string;
  data: Partial<Record<OfferingCategory, OfferingItem[]>>;
}

export interface OfferingOption {
  value: string;
  label: string;
  offeringId: number;
  offeringName: string;
  description: string | null;
  imageUrl: string | null;
  currency: string | null;
  amount: number | null;
  date: string | null;
  category: OfferingCategory;
}

export const OFFERINGS_QUERY_KEY = ["offerings", "general"] as const;

const OFFERING_CATEGORY_LABELS: Record<OfferingCategory, string> = {
  digitalProducts: "Product",
  productServices: "Service",
  liveEvents: "Live event",
  services: "Sessions",
  subscriptions: "Subscription",
};

type OfferingsError = {message?: string};

function readOfferingPrice(offering: OfferingItem) {
  const currencies = offering.currencies ?? [];
  const currencyRow =
    currencies.find((currency) => currency.isAutoConverted === false) ??
    currencies.find((currency) => currency.isBase) ??
    currencies[0];

  return {
    currency:
      currencyRow?.currency ??
      currencyRow?.callPriceCurrency ??
      offering.currency ??
      offering.priceCurrency ??
      offering.callPriceCurrency ??
      null,
    amount:
      currencyRow?.amount ??
      currencyRow?.callPrice ??
      offering.amount ??
      offering.price ??
      offering.callPrice ??
      null,
  };
}

function readOfferingDate(offering: OfferingItem) {
  return (
    offering.date ??
    offering.eventDate ??
    offering.startDate ??
    offering.activeSession?.startDate ??
    offering.sessionInfo?.[0]?.startDate ??
    offering.session_info?.[0]?.startDate ??
    null
  );
}

export function useGetOfferings() {
  const client = useClientV2();

  return useQuery<OfferingOption[], OfferingsError>({
    queryKey: OFFERINGS_QUERY_KEY,
    queryFn: async () => {
      const response = (await client("general/offerings")) as GetOfferingsResponse;

      return (Object.keys(response.data) as OfferingCategory[]).reduce<OfferingOption[]>(
        (allOfferings, category) => {
          const categoryOfferings = (response.data[category] ?? []).map((offering) => {
            const pricing = readOfferingPrice(offering);
            return {
              value: `${category}:${offering.id}`,
              label: `${offering.name} (${OFFERING_CATEGORY_LABELS[category]})`,
              offeringId: offering.id,
              offeringName: offering.name,
              description: offering.description ?? offering.about ?? null,
              imageUrl:
                offering.imageUrl ??
                offering.coverImage ??
                offering.coverPicture ??
                offering.coverImages?.[0]?.mediaUrl ??
                null,
              currency: pricing.currency,
              amount: pricing.amount,
              date: readOfferingDate(offering),
              category,
            };
          });

          return [...allOfferings, ...categoryOfferings];
        },
        []
      );
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useGetOfferingsRaw() {
  const client = useClientV2();

  return useQuery<GetOfferingsResponse["data"], OfferingsError>({
    queryKey: OFFERINGS_QUERY_KEY,
    queryFn: async () => {
      const response = (await client("general/offerings")) as GetOfferingsResponse;
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}
